import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Droplets, Link2, Plus, Thermometer, Trash2, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { usePopup } from "../context/PopupContext";
import { userApiService } from "../services/userApiService";
import {
  isDeviceOnline,
  listDeviceCommands,
  normalizeLastSeenMs,
  sendDeviceCommand,
} from "../services/deviceService";
import { derivePumpUiState, potLabelFromCommand } from "../utils/pumpActivityUtils";

function numFrom(live, ...keys) {
  if (!live || typeof live !== "object") return null;
  for (const k of keys) {
    const v = live[k];
    if (v === undefined || v === null || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function potLastSeenMs(hubId, nodeId, deviceLive = {}) {
  const nested = deviceLive?.[hubId]?.pots?.[nodeId] || {};
  const direct = deviceLive?.[nodeId] || {};
  const candidates = [
    normalizeLastSeenMs(nested.lastSeen),
    normalizeLastSeenMs(nested?.meta?.lastSeen),
    normalizeLastSeenMs(direct?.meta?.lastSeen),
    normalizeLastSeenMs(direct?.deviceStatus?.lastSeen),
  ].filter(Boolean);
  return candidates.length ? Math.max(...candidates) : null;
}

function readingsForNode(hubId, nodeId, deviceLive = {}, { online = true } = {}) {
  if (!online) {
    return { soil: null, temp: null, humidity: null, online: false };
  }
  // Prefer hub pot path — avoids showing a stale node_* Firebase leaf after the radio went offline
  const nested = deviceLive?.[hubId]?.pots?.[nodeId] || {};
  const direct = deviceLive?.[nodeId] || {};
  return {
    soil: numFrom(nested, "SoilMoisture", "soilMoisture", "s") ?? numFrom(direct, "SoilMoisture", "soilMoisture", "s"),
    temp: numFrom(nested, "Temperature", "temperature", "t") ?? numFrom(direct, "Temperature", "temperature", "t"),
    humidity: numFrom(nested, "Humidity", "humidity", "h") ?? numFrom(direct, "Humidity", "humidity", "h"),
    online: true,
  };
}

/**
 * Hub-only: linked pot nodes with per-pot soil + DHT (temp/humidity).
 */
const DRY_SOIL = 40;

export default function HubNodesPanel({
  hubId,
  linkedNodes = [],
  deviceLive = {},
  canEdit = false,
  pumpConnected = false,
  onChanged,
}) {
  const { showPopup } = usePopup();
  const [open, setOpen] = useState(false);
  const [nodeId, setNodeId] = useState("");
  const [nodeName, setNodeName] = useState("");
  const [busy, setBusy] = useState(false);
  const [commands, setCommands] = useState([]);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const refreshCommands = useCallback(async () => {
    if (!hubId) return;
    try {
      const data = await listDeviceCommands(hubId, { limit: 20 });
      setCommands(data?.items || []);
    } catch {
      /* non-blocking */
    }
  }, [hubId]);

  useEffect(() => {
    refreshCommands();
    const t = setInterval(refreshCommands, 2000);
    return () => clearInterval(t);
  }, [refreshCommands]);

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const pumpUi = useMemo(() => derivePumpUiState(commands, { nowMs }), [commands, nowMs]);
  const queue = useMemo(
    () =>
      (commands || [])
        .filter((c) => String(c.action).toLowerCase() === "on" && String(c.status).toLowerCase() === "queued")
        .sort((a, b) => Number(a.issuedAt || 0) - Number(b.issuedAt || 0)),
    [commands]
  );
  const wateringActive = pumpUi.running || pumpUi.phase === "sent" || pumpUi.phase === "sending";
  const nodes = useMemo(
    () => (Array.isArray(linkedNodes) ? linkedNodes : []).slice().sort((a, b) => (a.potIndex || 0) - (b.potIndex || 0)),
    [linkedNodes]
  );
  const activeCmd = pumpUi.activeCommand || null;
  const activeNodeId = String(activeCmd?.nodeId || "").trim();
  const activePotIndex = Number(activeCmd?.potIndex || 0) || 0;
  const wateringLabel = activeCmd ? potLabelFromCommand(activeCmd, nodes) : "";
  const nextQueueLabel = queue.length
    ? queue.map((c, i) => `#${i + 1} ${potLabelFromCommand(c, nodes)}`).join(" → ")
    : "";

  const linkNode = async () => {
    const id = nodeId.trim();
    if (!id) {
      toast.warning("Enter a node id like node_001");
      return;
    }
    setBusy(true);
    try {
      await userApiService.linkHubNode(hubId, { nodeId: id, name: nodeName.trim() });
      showPopup({
        type: "success",
        title: "Node linked",
        message: `${id} is now under this hub. Power the node nearby to finish ESP-NOW pairing.`,
        ttlMs: 4500,
      });
      toast.success("Node linked to hub");
      setOpen(false);
      setNodeId("");
      setNodeName("");
      onChanged?.();
    } catch (error) {
      showPopup({
        type: "error",
        title: "Could not link node",
        message: error?.message || "Link failed",
        ttlMs: 5000,
      });
      toast.error(error?.message || "Link failed");
    } finally {
      setBusy(false);
    }
  };

  const unlinkNode = async (id) => {
    if (!canEdit) return;
    setBusy(true);
    try {
      await userApiService.unlinkHubNode(hubId, id);
      showPopup({
        type: "success",
        title: "Node removed",
        message: `${id} was removed from this hub and can join another hub.`,
        ttlMs: 4000,
      });
      toast.success("Node removed");
      onChanged?.();
    } catch (error) {
      toast.error(error?.message || "Remove failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-cream/30 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium flex items-center gap-2">
            <Link2 className="h-4 w-4 text-accent" />
            Pot nodes
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Each pot has soil + temp + humidity. Tap Water anytime — if the pump is busy, your pot joins the queue.
            {wateringActive && wateringLabel ? (
              <span className="mt-1 block font-medium text-accent">
                Watering now: {wateringLabel}
                {pumpUi.remainingSec != null ? ` · ${pumpUi.remainingSec}s left` : ""}
              </span>
            ) : wateringActive ? (
              <span className="mt-1 block text-accent">
                Watering now ({pumpUi.badge}
                {pumpUi.remainingSec != null ? ` · ${pumpUi.remainingSec}s left` : ""}).
              </span>
            ) : null}
            {nextQueueLabel ? (
              <span className="mt-1 block text-foreground">Next: {nextQueueLabel}</span>
            ) : null}
          </p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setOpen(true)} disabled={busy}>
              <Plus className="h-4 w-4" /> Add node
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await userApiService.addHubSimNodes(hubId);
                  toast.success("Added simulator pots (dry + wet)");
                  onChanged?.();
                } catch (error) {
                  toast.error(error?.message || "Could not add simulator pots");
                } finally {
                  setBusy(false);
                }
              }}
            >
              Add test nodes
            </Button>
          </div>
        )}
      </div>

      <div className="mt-3 grid gap-2">
        {nodes.map((n) => {
          const online =
            String(n.nodeId || "").includes("_sim_") ||
            isDeviceOnline(potLastSeenMs(hubId, n.nodeId, deviceLive), nowMs);
          const r = readingsForNode(hubId, n.nodeId, deviceLive, { online });
          const isWateringNow =
            Boolean(activeCmd) &&
            ((activeNodeId && String(n.nodeId) === activeNodeId) ||
              (!activeNodeId && activePotIndex && Number(n.potIndex) === activePotIndex));
          const queuePos = queue.findIndex(
            (c) =>
              (c.nodeId && String(c.nodeId) === String(n.nodeId)) ||
              (Number(c.potIndex || 0) > 0 && Number(c.potIndex) === Number(n.potIndex || 0))
          );
          return (
            <div
              key={n.nodeId}
              className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm ${
                isWateringNow
                  ? "border-success/50 bg-success/10"
                  : online
                    ? "border-border bg-card"
                    : "border-border/70 bg-muted/20 opacity-90"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="font-medium">{n.name || n.nodeId}</div>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      online ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {online ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                    {online ? "Online" : "Offline"}
                  </span>
                  {isWateringNow ? (
                    <span className="inline-flex items-center rounded-full bg-success/20 px-2 py-0.5 text-[10px] font-medium text-success">
                      Watering now
                    </span>
                  ) : queuePos >= 0 ? (
                    <span className="inline-flex items-center rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-medium text-accent">
                      Next #{queuePos + 1}
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-muted-foreground">
                  {n.nodeId}
                  {n.potIndex ? ` · Pot ${n.potIndex}` : ""}
                  {String(n.nodeId || "").includes("_sim_") ? " · test data only" : ""}
                  {!online ? " · no recent cloud update" : ""}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="min-w-[3.75rem] rounded-xl border border-border bg-cream/50 px-2.5 py-1.5 text-right">
                  <div className="flex items-center justify-end gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <Thermometer className="h-3 w-3" /> Temp
                  </div>
                  <div className="font-display text-base leading-tight">
                    {r.temp != null ? r.temp.toFixed(1) : "—"}
                    {r.temp != null ? <span className="text-xs text-muted-foreground">°C</span> : null}
                  </div>
                </div>
                <div className="min-w-[3.75rem] rounded-xl border border-border bg-cream/50 px-2.5 py-1.5 text-right">
                  <div className="flex items-center justify-end gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <Droplets className="h-3 w-3" /> Hum
                  </div>
                  <div className="font-display text-base leading-tight">
                    {r.humidity != null ? Math.round(r.humidity) : "—"}
                    {r.humidity != null ? <span className="text-xs text-muted-foreground">%</span> : null}
                  </div>
                </div>
                <div className="min-w-[3.75rem] rounded-xl border border-border bg-cream/50 px-2.5 py-1.5 text-right">
                  <div className="flex items-center justify-end gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <Droplets className="h-3 w-3" /> Soil
                  </div>
                  <div className="font-display text-base leading-tight">
                    {r.soil != null ? Math.round(r.soil) : "—"}
                    {r.soil != null ? <span className="text-xs text-muted-foreground">%</span> : null}
                  </div>
                  {r.soil != null && r.soil < DRY_SOIL ? (
                    <div className="text-[10px] text-accent">Needs water</div>
                  ) : null}
                </div>
                {canEdit && pumpConnected && !String(n.nodeId || "").includes("_sim_") && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || !online}
                    title={
                      !online
                        ? "Node offline — power it near the hub and wait for soil reports"
                        : wateringActive
                          ? "Pump busy — this pot will be queued"
                          : "Water this pot"
                    }
                    onClick={async () => {
                      if (!online) {
                        toast.warning("That pot node is offline — check power and ESP-NOW pairing");
                        return;
                      }
                      setBusy(true);
                      try {
                        const result = await sendDeviceCommand(hubId, {
                          target: "pump",
                          action: "on",
                          durationSec: 60,
                          nodeId: n.nodeId,
                          potIndex: n.potIndex,
                        });
                        if (result.queued) {
                          toast.message(result.message || `Queued — will water ${n.name || n.nodeId} next`);
                        } else {
                          toast.success(`Watering ${n.name || n.nodeId} (valve + pump)`);
                        }
                        await refreshCommands();
                      } catch (error) {
                        const msg = error?.message || "Could not start watering";
                        if (/already running|in progress|in_flight|pump_running/i.test(msg)) {
                          toast.message("Pump still finishing — try again in a few seconds (auto-clears ~10s after end)");
                        } else {
                          toast.error(msg);
                        }
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Water
                  </Button>
                )}
                {canEdit && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive"
                    disabled={busy}
                    onClick={() => unlinkNode(n.nodeId)}
                  >
                    <Trash2 className="h-4 w-4" /> Remove
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        {!nodes.length && (
          <div className="rounded-xl border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
            No pot nodes linked yet. Ask Super Admin to register node_001, then use Add node.
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-6">
          <div className="w-full max-w-md rounded-3xl border border-border bg-card p-6 shadow-elevated">
            <div className="font-display text-2xl">Add pot node</div>
            <p className="mt-2 text-sm text-muted-foreground">
              Enter the node id (example: <span className="font-mono">node_001</span>). It must already be
              registered by Super Admin.
            </p>
            <input
              className="mt-4 w-full rounded-2xl border border-input bg-cream/50 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              placeholder="node_001"
              value={nodeId}
              onChange={(e) => setNodeId(e.target.value)}
            />
            <input
              className="mt-3 w-full rounded-2xl border border-input bg-cream/50 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              placeholder="Display name (optional)"
              value={nodeName}
              onChange={(e) => setNodeName(e.target.value)}
            />
            <div className="mt-5 flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
                Cancel
              </Button>
              <Button onClick={linkNode} disabled={busy}>
                Link to hub
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
